import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { notesAPI, notebooksAPI, tagsAPI, templatesAPI, remindersAPI, exportAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { encryptContent } from '@/lib/crypto';
import { parseSearchQuery, applySearchFilters } from '@/lib/searchParser';
import * as offlineDB from '@/lib/offlineDB';
import { toast } from 'sonner';

// Extracted hooks
import { useEncryption } from '@/hooks/useEncryption';
import { useSyncActions } from '@/hooks/useSyncActions';
import { useNoteActions } from '@/hooks/useNoteActions';
import { useJournalActions } from '@/hooks/useJournalActions';
import { useLiveSync } from '@/hooks/useLiveSync';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { encryptionKey, encryptionPassword, isEncryptionEnabled, user } = useAuth();

  // ── Shared state ──────────────────────────────────────────────
  const [notes, setNotes] = useState([]);
  const [notebooks, setNotebooks] = useState([]);
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [activeView, setActiveView] = useState('all');
  const [filterNotebookId, setFilterNotebookId] = useState(null);
  const [filterTagId, setFilterTagId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editorMode, setEditorMode] = useState('split');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [includeJournalInAll, setIncludeJournalInAll] = useState(false);
  const [publishDialogRequested, setPublishDialogRequested] = useState(false);
  const isSavedRef = useRef(true);

  // Session ID for cross-device sync (generated once per tab)
  const sessionIdRef = useRef(null);
  useEffect(() => {
    let id = sessionStorage.getItem('vn_session_id');
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem('vn_session_id', id);
    }
    sessionIdRef.current = id;
  }, []);

  // ── Hook 1: Encryption ────────────────────────────────────────
  const { decryptNote, decryptAllNotes, encryptExistingNote } = useEncryption({
    encryptionKey, encryptionPassword, user, notes, setNotes,
  });

  // ── Hook 2: Sync / Offline ────────────────────────────────────
  const {
    isOnline, pendingSyncCount, setPendingSyncCount,
    syncConflict, syncPendingChanges, resolveSyncConflict, dismissSyncConflict,
  } = useSyncActions({ setNotes });

  // ── Data fetchers ─────────────────────────────────────────────
  const fetchNotes = useCallback(async () => {
    try {
      const res = await notesAPI.list();
      let allNotes = res.data;
      allNotes = await Promise.all(allNotes.map(decryptNote));
      setNotes(allNotes);
      await offlineDB.saveNotes(allNotes);
      await offlineDB.setLastSyncTime();
    } catch (err) {
      console.error('Failed to fetch notes from server, loading offline data...', err);
      if (!navigator.onLine) {
        try {
          const offlineNotes = await offlineDB.getAllNotes();
          const decryptedNotes = await Promise.all(offlineNotes.map(decryptNote));
          setNotes(decryptedNotes);
          toast.info('Loaded notes from offline storage');
        } catch (dbErr) {
          console.error('Failed to load offline notes:', dbErr);
        }
      }
    }
  }, [decryptNote]);

  const fetchNotebooks = useCallback(async () => {
    try {
      const res = await notebooksAPI.list();
      setNotebooks(res.data);
      await offlineDB.saveNotebooks(res.data);
    } catch (err) {
      console.error('Failed to fetch notebooks', err);
      if (!navigator.onLine) {
        const offlineNotebooks = await offlineDB.getAllNotebooks();
        setNotebooks(offlineNotebooks);
      }
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const res = await tagsAPI.list();
      setTags(res.data);
      await offlineDB.saveTags(res.data);
    } catch (err) {
      console.error('Failed to fetch tags', err);
      if (!navigator.onLine) {
        const offlineTags = await offlineDB.getAllTags();
        setTags(offlineTags);
      }
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try { const res = await templatesAPI.list(); setTemplates(res.data); }
    catch (err) { console.error('Failed to fetch templates', err); }
  }, []);

  const fetchReminders = useCallback(async () => {
    try { const res = await remindersAPI.list(); setReminders(res.data); }
    catch (err) { console.error('Failed to fetch reminders', err); }
  }, []);

  // ── Hook 3: Note CRUD ─────────────────────────────────────────
  const {
    createNote, updateNote, deleteNote, permanentDeleteNote, emptyTrash,
    archiveNote, restoreNote, togglePin, toggleFavorite,
    shareNote, unshareNote, updateNoteLocal,
  } = useNoteActions({
    notes, setNotes, selectedNoteId, setSelectedNoteId,
    decryptNote, isEncryptionEnabled, encryptionKey, user,
    notebooks, templates, setPendingSyncCount, sessionIdRef,
  });

  // ── Hook 4: Journal ───────────────────────────────────────────
  const {
    journalNotebookId, setJournalNotebookId,
    ensureJournalNotebook, createJournalEntry, journalNotes,
  } = useJournalActions({
    notebooks, templates, user, createNote, fetchNotebooks, setActiveView, notes,
  });

  // ── Hook 5: Live Sync (cross-device freshness) ─────────────────
  const {
    staleNoteEvent,
    dismissStaleNote,
    acceptServerVersion,
  } = useLiveSync({
    notes, setNotes, decryptNote, selectedNoteId, isSavedRef, sessionIdRef,
  });

  // ── Notebook / Tag / Template / Reminder CRUD ─────────────────
  const createNotebook = useCallback(async (data) => {
    try {
      const res = await notebooksAPI.create(data);
      setNotebooks((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success('Notebook created');
      return res.data;
    } catch { toast.error('Failed to create notebook'); }
  }, []);

  const updateNotebook = useCallback(async (id, data) => {
    try {
      const res = await notebooksAPI.update(id, data);
      setNotebooks((prev) => prev.map((n) => (n.id === id ? res.data : n)));
      toast.success('Notebook updated');
    } catch { toast.error('Failed to update notebook'); }
  }, []);

  const deleteNotebook = useCallback(async (id) => {
    try {
      await notebooksAPI.delete(id);
      setNotebooks((prev) => prev.filter((n) => n.id !== id));
      if (filterNotebookId === id) setFilterNotebookId(null);
      toast.success('Notebook deleted');
    } catch { toast.error('Failed to delete notebook'); }
  }, [filterNotebookId]);

  const createTag = useCallback(async (data) => {
    try {
      const res = await tagsAPI.create(data);
      setTags((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success('Tag created');
      return res.data;
    } catch { toast.error('Failed to create tag'); }
  }, []);

  const updateTag = useCallback(async (id, data) => {
    try {
      const res = await tagsAPI.update(id, data);
      setTags((prev) => prev.map((t) => (t.id === id ? res.data : t)));
      toast.success('Tag updated');
    } catch { toast.error('Failed to update tag'); }
  }, []);

  const deleteTag = useCallback(async (id) => {
    try {
      await tagsAPI.delete(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
      if (filterTagId === id) setFilterTagId(null);
      toast.success('Tag deleted');
    } catch { toast.error('Failed to delete tag'); }
  }, [filterTagId]);

  const createTemplate = useCallback(async (data) => {
    try {
      const res = await templatesAPI.create(data);
      setTemplates((prev) => [...prev, res.data]);
      toast.success('Template created');
      return res.data;
    } catch { toast.error('Failed to create template'); }
  }, []);

  const updateTemplate = useCallback(async (id, data) => {
    try {
      const res = await templatesAPI.update(id, data);
      setTemplates((prev) => prev.map((t) => (t.id === id ? res.data : t)));
      toast.success('Template updated');
    } catch { toast.error('Failed to update template'); }
  }, []);

  const deleteTemplate = useCallback(async (id) => {
    try {
      await templatesAPI.delete(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success('Template deleted');
    } catch { toast.error('Failed to delete template'); }
  }, []);

  const createReminder = useCallback(async (data) => {
    try {
      const res = await remindersAPI.create(data);
      setReminders((prev) => [...prev, res.data]);
      toast.success('Reminder set');
      return res.data;
    } catch { toast.error('Failed to create reminder'); }
  }, []);

  const deleteReminder = useCallback(async (id) => {
    try {
      await remindersAPI.delete(id);
      setReminders((prev) => prev.filter((r) => r.id !== id));
    } catch { toast.error('Failed to delete reminder'); }
  }, []);

  const completeReminder = useCallback(async (id) => {
    try {
      await remindersAPI.complete(id);
      setReminders((prev) => prev.map((r) => (r.id === id ? { ...r, is_completed: true } : r)));
    } catch { toast.error('Failed to complete reminder'); }
  }, []);

  // ── Export / Import ───────────────────────────────────────────
  const doExportNotes = useCallback(async () => {
    try { const res = await exportAPI.exportNotes(); return res.data; }
    catch { toast.error('Failed to export notes'); }
  }, []);

  const doImportNotes = useCallback(async (data) => {
    try {
      const res = await exportAPI.importNotes(data);
      await fetchNotes();
      toast.success(`Imported ${res.data.imported} notes`);
      return res.data;
    } catch { toast.error('Failed to import notes'); }
  }, [fetchNotes]);

  // ── Effects ───────────────────────────────────────────────────

  // Initial data fetch
  useEffect(() => {
    Promise.all([fetchNotes(), fetchNotebooks(), fetchTags(), fetchTemplates(), fetchReminders()])
      .then(() => {
        notebooksAPI.list().then(res => {
          const jnb = res.data.find(nb => nb.name === 'Journal');
          if (jnb) setJournalNotebookId(jnb.id);
        }).catch(() => {});
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-decrypt notes when encryption key becomes available
  useEffect(() => {
    if (encryptionKey && notes.some((n) => n.is_encrypted && (!n._decrypted || n._needsDecryption || n._decryptFailed))) {
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encryptionKey]);

  // Retroactively encrypt unencrypted notes when encryption is enabled
  useEffect(() => {
    if (!isEncryptionEnabled || !encryptionKey) return;
    const currentSalt = user?.encryption_salt;
    if (!currentSalt) return;
    const unencrypted = notes.filter((n) => !n.is_encrypted && n.content && !n._encryptingInProgress);
    if (unencrypted.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const note of unencrypted) {
        if (cancelled) break;
        setNotes((prev) => prev.map((n) => n.id === note.id ? { ...n, _encryptingInProgress: true } : n));
        const result = await encryptExistingNote(note, encryptionKey, currentSalt);
        if (!cancelled) {
          setNotes((prev) => prev.map((n) => n.id === note.id ? result : n));
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEncryptionEnabled, encryptionKey, notes.length]);

  // ── Computed values ───────────────────────────────────────────
  const selectedNote = useMemo(() => notes.find((n) => n.id === selectedNoteId), [notes, selectedNoteId]);

  const filteredNotes = useMemo(() => {
    let filtered = notes;

    switch (activeView) {
      case 'favorites':
        filtered = filtered.filter((n) => n.is_favorited && !n.is_archived && !n.is_deleted);
        break;
      case 'archived':
        filtered = filtered.filter((n) => n.is_archived && !n.is_deleted);
        break;
      case 'trash':
        filtered = filtered.filter((n) => n.is_deleted);
        break;
      default:
        filtered = filtered.filter((n) => !n.is_archived && !n.is_deleted);
    }

    if (activeView === 'all' && !filterNotebookId && !filterTagId && !searchQuery && !includeJournalInAll) {
      filtered = filtered.filter((n) => !n.is_journal);
    }

    if (filterNotebookId) filtered = filtered.filter((n) => n.notebook_id === filterNotebookId);
    if (filterTagId) filtered = filtered.filter((n) => n.tags && n.tags.includes(filterTagId));

    if (searchQuery) {
      const parsed = parseSearchQuery(searchQuery, tags, notebooks);
      filtered = applySearchFilters(filtered, parsed);
    }

    return filtered.sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
  }, [notes, activeView, filterNotebookId, filterTagId, searchQuery, tags, notebooks, includeJournalInAll]);

  const noteCountByNotebook = useMemo(() => {
    const counts = {};
    notes.filter((n) => !n.is_archived && !n.is_deleted).forEach((n) => {
      if (n.notebook_id) counts[n.notebook_id] = (counts[n.notebook_id] || 0) + 1;
    });
    return counts;
  }, [notes]);

  const noteCountByTag = useMemo(() => {
    const counts = {};
    notes.filter((n) => !n.is_archived && !n.is_deleted).forEach((n) => {
      (n.tags || []).forEach((tagId) => { counts[tagId] = (counts[tagId] || 0) + 1; });
    });
    return counts;
  }, [notes]);

  // ── Context value ─────────────────────────────────────────────
  const contextValue = useMemo(() => ({
    notes, filteredNotes, selectedNote, selectedNoteId, setSelectedNoteId,
    activeView, setActiveView,
    filterNotebookId, setFilterNotebookId,
    filterTagId, setFilterTagId,
    searchQuery, setSearchQuery,
    editorMode, setEditorMode,
    sidebarOpen, setSidebarOpen,
    notebooks, tags, templates, reminders,
    noteCountByNotebook, noteCountByTag,
    loading,
    createNote, updateNote, deleteNote, permanentDeleteNote, emptyTrash,
    archiveNote, restoreNote, togglePin, toggleFavorite,
    createNotebook, updateNotebook, deleteNotebook,
    createTag, updateTag, deleteTag,
    createTemplate, updateTemplate, deleteTemplate,
    createReminder, deleteReminder, completeReminder,
    shareNote, unshareNote, updateNoteLocal,
    doExportNotes, doImportNotes,
    fetchNotes, fetchNotebooks, fetchTags,
    decryptAllNotes,
    // Journal
    journalNotes, journalNotebookId, createJournalEntry, ensureJournalNotebook,
    includeJournalInAll, setIncludeJournalInAll,
    // Offline state
    isOnline,
    pendingSyncCount,
    syncPendingChanges,
    // Sync conflict
    syncConflict,
    resolveSyncConflict,
    dismissSyncConflict,
    // Publish dialog trigger
    publishDialogRequested,
    setPublishDialogRequested,
    // Live sync (cross-device freshness)
    isSavedRef,
    sessionIdRef,
    staleNoteEvent,
    dismissStaleNote,
    acceptServerVersion,
  }), [
    notes, filteredNotes, selectedNote, selectedNoteId,
    activeView, filterNotebookId, filterTagId, searchQuery,
    editorMode, sidebarOpen, notebooks, tags, templates, reminders,
    noteCountByNotebook, noteCountByTag, loading,
    createNote, updateNote, deleteNote, permanentDeleteNote, emptyTrash,
    archiveNote, restoreNote, togglePin, toggleFavorite,
    createNotebook, updateNotebook, deleteNotebook,
    createTag, updateTag, deleteTag,
    createTemplate, updateTemplate, deleteTemplate,
    createReminder, deleteReminder, completeReminder,
    shareNote, unshareNote, updateNoteLocal,
    doExportNotes, doImportNotes,
    fetchNotes, fetchNotebooks, fetchTags,
    decryptAllNotes,
    journalNotes, journalNotebookId, createJournalEntry, ensureJournalNotebook,
    includeJournalInAll,
    isOnline, pendingSyncCount, syncPendingChanges,
    syncConflict, resolveSyncConflict, dismissSyncConflict,
    publishDialogRequested,
    staleNoteEvent, dismissStaleNote, acceptServerVersion,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
